import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { StatNavItem, SeasonNavItem } from './components/nav-items';
import { Heading, DataTypeToggle } from './components/header';
import {
    BenchmarkingExplainer,
    BenchmarkingLegend
} from 'common/react/components/benchmarking';
import { PerformanceChartContainer } from './components/chart';

import { usePerformanceChartNavSync } from './hooks/use-performance-chart-nav-sync';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';

import {
    performanceChartDataTypeSelector,
    performanceChartNavSelector
} from 'store/modules/statspro/performance-chart/selectors';
import classNames from 'classnames';
import { playersSelector } from 'store/modules/statspro/player-comparison/selectors';
import { BenchmarkingPlaceholder } from 'common/react/components/benchmarking/BenchmarkingPlaceholder';

export const PerformanceChart = ({ title, type }) => {
    const nav = useSelector(performanceChartNavSelector);
    const options = useCompetitionNavigationOptions(nav);
    usePerformanceChartNavSync(options);
    const playerId = new URLSearchParams(location.search).get('playerId');
    const playerIdsFromStore = useSelector(playersSelector);
    const dataType = useSelector(performanceChartDataTypeSelector);
    const [showBenchmarking, setShowBenchmarking] = useState(true);
    const ref = useRef();

    const playerIds = useMemo(
        () => (playerId ? { 1: playerId, 2: null } : playerIdsFromStore),
        [playerId, playerIdsFromStore]
    );

    useEffect(() => {
        if (nav.season.id !== -1) {
            if (dataType.type === 'totals') {
                setShowBenchmarking(false);
            } else {
                setShowBenchmarking(true);
            }
        } else {
            setShowBenchmarking(true);
        }
    }, [dataType, nav.season.id]);

    return (
        <div
            className={classNames('fade-in-on-load', {
                'fade-in-on-load fade-in-on-load--stats comparison-chart__wrapper':
                    !playerId,
                'is-loaded': playerIds[1] || playerIds[2]
            })}
            ref={ref}
        >
            {playerIds[1] || playerIds[2] ? (
                <StatsSection.Main>
                    <StatsSection.Heading isSmallModifier={true}>
                        {title}
                    </StatsSection.Heading>
                    <StatsSection.Nav>
                        <StatsSection.NavItems>
                            <StatNavItem />
                            <SeasonNavItem />
                        </StatsSection.NavItems>
                    </StatsSection.Nav>
                    <StatsSection.Content>
                        <StatsSection.ContentHeader>
                            <StatsSection.ContentHeaderHeading>
                                <Heading />
                            </StatsSection.ContentHeaderHeading>
                            <StatsSection.ContentHeaderOptions>
                                <DataTypeToggle />
                            </StatsSection.ContentHeaderOptions>
                            {showBenchmarking ? (
                                <StatsSection.ContentHeaderBelow>
                                    <BenchmarkingLegend />
                                    <BenchmarkingExplainer />
                                </StatsSection.ContentHeaderBelow>
                            ) : (
                                <StatsSection.ContentHeaderBelow
                                    isSingleModifier={true}
                                >
                                    <BenchmarkingPlaceholder />
                                </StatsSection.ContentHeaderBelow>
                            )}
                        </StatsSection.ContentHeader>
                        <StatsSection.ContentBody>
                            <PerformanceChartContainer
                                playerIds={playerIds}
                                type={type}
                                containerRef={ref}
                            />
                        </StatsSection.ContentBody>
                    </StatsSection.Content>
                </StatsSection.Main>
            ) : null}
        </div>
    );
};

PerformanceChart.propTypes = {
    title: PropTypes.string.isRequired,
    type: PropTypes.string
};
